import {
  isBlankConditionValue,
  normalizeFormulaOperators,
} from 'containers/OfferLetterTemplates/constants';

import { FormField, OfferLetterFormData } from './types';

/**
 * Evaluates a template field's one-hop formula (AXHE-4543) — a reference to
 * another number/currency field's own `key`, an operator, and an operand, e.g.
 * "WEEKLY_SALARY * 2". The editor's picklist (VariableRow.tsx) excludes any
 * field that itself carries a formula from being referenced, so a formula
 * never chains and this never has to resolve more than one hop.
 *
 * A computed value is submitted back as an ordinary entry
 * (`effectiveValues()`, index.tsx) — `MergeValueResolver#raw_value_for`
 * returns whatever arrives there verbatim, before `format_value` branches on
 * `currency_format` / `spell_out`. That is what keeps this a client-side
 * concern: the server never evaluates a formula, it only ever sees the result.
 */

// KEY <operator> <operand> — mirrors the server's own
// `OfferLetterTemplates::FormulaValidator::FORMULA_PATTERN`, which is the one
// gate a formula passes through before it can be stored today. It was not
// always the gate, though: `formulas` carried no server validation before
// this ticket, and the free-text hint it replaces told admins to type
// exactly `+ − × ÷` — so a formula already stored with one of those Unicode
// characters is live data, not a hypothetical. Normalizing here (not just at
// authoring time) is what keeps that data readable.
const FORMULA_PATTERN = /^([A-Za-z_][A-Za-z0-9_]*)\s*([+\-*/])\s*(-?\d+(?:\.\d+)?)$/;

type Operator = '+' | '-' | '*' | '/';

interface ParsedFormula {
  key: string;
  operator: Operator;
  operand: number;
}

const parseFormula = (formula: string): ParsedFormula | null => {
  const match = FORMULA_PATTERN.exec(normalizeFormulaOperators(formula).trim());
  if (!match) return null;

  return { key: match[1], operator: match[2] as Operator, operand: Number(match[3]) };
};

// A recruiter can type (or a mapped value can resolve to) a grouped number —
// "100,000" — same as the pre-existing manual-entry hazard `isNumericField`
// documents. `Number()` alone would reject it as NaN and blank the derived
// field with no diagnostic; this scoped fix is for the formula path only,
// not a fix to that broader, pre-existing gap.
//
// Only a comma that is unambiguously a thousands separator is stripped: every
// comma groups exactly three digits ("100,000", "1,000,000"). A comma in any
// other position is a decimal separator in some locales ("75000,50" is 75000.5,
// not 7500050) and a thousands separator in others — which one is a locale
// question this screen deliberately does not answer (see `isNumericField` in
// SendOfferForm.tsx). Rather than guess and submit a materially wrong amount
// onto a legal document, treat it as unresolvable: `NaN` makes the formula fall
// back to `field.value`, the honest rendering.
const GROUPED_THOUSANDS = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/;

const parseNumeric = (value: string): number => {
  const trimmed = value.trim();
  if (!trimmed.includes(',')) return Number(trimmed);
  return GROUPED_THOUSANDS.test(trimmed)
    ? Number(trimmed.replace(/,/g, ''))
    : NaN;
};

const applyOperator = (operator: Operator, a: number, b: number): number => {
  switch (operator) {
    case '+':
      return a + b;
    case '-':
      return a - b;
    case '*':
      return a * b;
    case '/':
      return a / b;
    default:
      return NaN;
  }
};

/**
 * The current answer for a field — what the recruiter typed, else the
 * server's prefill. A blank result is "not answered", the same rule
 * `resolveDataKeyValues` (visibility.ts) applies for condition evaluation —
 * it is this module's "unresolvable" signal.
 */
const currentValue = (field: FormField, values: Record<string, string>): string | undefined => {
  const current = values[field.key] ?? field.value;
  return isBlankConditionValue(current) ? undefined : current;
};

/**
 * Groups every field by its lowercased key, so a case-insensitive reference
 * (see `resolveReference`) can tell a real match from an ambiguous one. The
 * uniqueness index on `key` is a plain btree (`idx_..._on_template_and_key`),
 * not `lower(key)`, so `Weekly_Salary` and `WEEKLY_SALARY` can coexist on the
 * same template.
 */
const fieldsByLowerKey = (form: OfferLetterFormData): Map<string, FormField[]> => {
  const byLowerKey = new Map<string, FormField[]>();
  form.fields.forEach(field => {
    const lowerKey = field.key.toLowerCase();
    byLowerKey.set(lowerKey, [...(byLowerKey.get(lowerKey) ?? []), field]);
  });
  return byLowerKey;
};

/**
 * The field a formula's reference resolves to. Exact match first — authoring
 * through the picklist always stores the referenced field's own exact-case
 * key, so this is the common case. Falls back to a case-insensitive match
 * only when it is unambiguous: two fields differing only by case is a real
 * possibility the database does not prevent (see `fieldsByLowerKey`), and
 * guessing between them risks silently resolving to the wrong one. An
 * ambiguous match is treated the same as no match — unresolvable.
 */
const resolveReference = (
  key: string,
  form: OfferLetterFormData,
  byLowerKey: Map<string, FormField[]>,
): FormField | undefined => {
  const exact = form.fields.find(field => field.key === key);
  if (exact) return exact;

  const candidates = byLowerKey.get(key.toLowerCase()) ?? [];
  return candidates.length === 1 ? candidates[0] : undefined;
};

/**
 * A bare decimal, never grouped, and rounded to 2 places regardless of
 * `data_type`. `CurrencyWriter` does `BigDecimal(amount.to_s)`, which raises
 * on a thousands separator — a comma would be rescued by `format_value` into
 * the raw, unformatted string rather than a currency-rendered one. 2 places
 * for a plain `number` field too is a deliberate choice, not an oversight:
 * the alternative (more precision for non-currency) is what let
 * `100000 / 12` print `8333.333333` on a signed letter — `Number(...).
 * toString()` still drops a trailing `.00` so a whole number stays `2000`,
 * not `2000.00`.
 */
const formatComputed = (value: number): string => Number(value.toFixed(2)).toString();

/**
 * Evaluates every field's formula against the current answers, keyed by the
 * field's own `key`. A field with no formula, an unparseable one (shouldn't
 * happen — FormulaValidator is the gate before one is ever stored, but a
 * template written before that existed is still live data), an unresolvable
 * or ambiguous reference, or a non-numeric referenced value is simply absent
 * from the result — the caller's cue to fall back to `field.value`.
 */
export const resolveFormulas = (
  form: OfferLetterFormData,
  values: Record<string, string>,
): Record<string, string> => {
  const byLowerKey = fieldsByLowerKey(form);
  const resolved: Record<string, string> = {};

  form.fields.forEach(field => {
    const [formula] = field.formulas;
    if (!formula) return;

    const parsed = parseFormula(formula);
    if (!parsed) return;

    const referencedField = resolveReference(parsed.key, form, byLowerKey);
    if (!referencedField) return;

    // `FormulaValidator` enforces the one-hop rule (no self-reference, no
    // referencing or being referenced by another formula field) on WRITE —
    // when a formula is authored through the picklist. It does not re-check
    // rows stored before that validator existed, and this evaluator runs on
    // every submission regardless of when the row was written. Without this,
    // a legacy self-reference doubles whatever the recruiter typed into the
    // field it derives, and a legacy chain (C -> B -> A, each one hop from
    // the last) silently computes only the first hop and leaves the rest
    // absent — both on a document about to be signed.
    if (referencedField.key.toLowerCase() === field.key.toLowerCase()) return;
    if (referencedField.formulas.length > 0) return;

    const referencedValue = currentValue(referencedField, values);
    if (referencedValue === undefined) return;

    const referencedNumber = parseNumeric(referencedValue);
    if (Number.isNaN(referencedNumber)) return;

    const computed = applyOperator(parsed.operator, referencedNumber, parsed.operand);
    if (!Number.isFinite(computed)) return;
    // Past `Number.MAX_SAFE_INTEGER`, `toFixed`/`toString` can fall back to
    // exponent notation ("1e+22") — CurrencyWriter's SCALES table stops at
    // Trillion, several orders below where this starts, and the raw string
    // would otherwise submit verbatim onto the letter. A recruiter-typed
    // value this large is already implausible; the honest response is to
    // leave the field unresolved rather than print scientific notation on a
    // signed document.
    if (!Number.isSafeInteger(Math.trunc(computed))) return;

    resolved[field.key] = formatComputed(computed);
  });

  return resolved;
};

/**
 * What a field currently shows and should submit, including a formula's
 * fallback. A blank entry is treated as untouched for a formula field: not
 * present at all (`values[field.key]` absent) and a manually cleared field
 * (`values[field.key] === ''`) mean the same thing here, matching
 * `isBlankConditionValue`'s "not answered" rule everywhere else on this form.
 * Without this, clearing a computed field disabled it for the rest of the
 * session — the hint disappeared, it stopped tracking its reference, and
 * every later submission carried '' (AXHE-4543 review) — because `??` only
 * treats `null`/`undefined` as "no entry", not an empty string.
 *
 * A field with no formula is untouched by this: it always shows exactly
 * `values[field.key] ?? field.value`, so clearing an ordinary field still
 * submits blank rather than resurrecting a prefill.
 */
export const effectiveFormulaValue = (
  field: FormField,
  values: Record<string, string>,
  formulaValues: Record<string, string>,
): string => {
  if (field.formulas.length > 0 && isBlankConditionValue(values[field.key])) {
    return formulaValues[field.key] ?? field.value;
  }
  return values[field.key] ?? field.value;
};
